<template>
  <div
    ref="tooltipComponentRef"
    class="tooltip-component"
    :class="{ open: tooltipOpen }"
    v-bind="$attrs"
  >
    <div
      class="tooltip-component__label"
      aria-describedby="tooltip-demo-content"
      data-tooltip-placeholder
      ref="placeholder"
      @mouseover.prevent="handleMousover"
      @mouseout="handleMouseout"
      @touchStart="toggle"
      @click.stop="handleClick"
    >
      <slot name="toggle"></slot>
    </div>
    <div class="tooltip-component-dropdown" data-tooltip-dropdown ref="dropdown">
      <div
        ref="dropdownContent"
        role="tooltip"
        id="tooltip-demo-content"
        class="tooltip-component-dropdown__content"
        :location="location"
      >
        <slot> </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, defineProps } from "vue";

const props = defineProps({
  name: { type: String, default: "tooltip" },
  screenPadding: { type: Number, default: 16 },
  location: { type: String },
});
const tooltipComponentRef = ref();
const placeholder = ref();
const dropdown = ref();
const tooltipOpen = ref();
const dropdownContent = ref();

function handleDropdownPosition() {
  const screenPadding = 16;
  const placeholderRect = placeholder.value?.getBoundingClientRect();
  const windowWidth = window.innerWidth;
  const availableWidth = windowWidth - placeholderRect.x - placeholderRect.width - screenPadding;

  if (props.location !== "left") {
    tooltipComponentRef.value.style.minWidth = `${availableWidth - 16}px`;
    tooltipComponentRef.value.style.maxWidth = "300px";
  }
}
function handleMousover() {
  if (window.innerWidth > 576) {
    toggle();
  }
}

function handleMouseout() {
  if (window.innerWidth > 576) {
    close();
  }
}

function handleClick() {
  if (window.innerWidth <= 576) {
    toggle();
  }
}

function toggle() {
  if (tooltipOpen.value === true) {
    close();
  } else {
    open();
  }
}

function open() {
  handleDropdownPosition();
  tooltipOpen.value = true;
}

function close() {
  tooltipOpen.value = false;
}

function handleClickOutside(event: any) {
  if (!(tooltipComponentRef.value as HTMLElement).contains(event.target)) {
    tooltipOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style lang="scss" scoped>
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

body {
  padding: 16px;
}

.tooltip-component {
  display: inline-flex;
  position: absolute;
  z-index: 2;
}

.tooltip-component:hover,
.tooltip-component:focus,
.tooltip-component.open {
  z-index: 9000;
}
.tooltip-component__label {
  &:before {
    // for the help cursor
    content: "";
    display: flex;
    cursor: help;
    position: absolute;
  }

  cursor: help;
}

.tooltip-component-dropdown {
  display: none;
  position: absolute;
  top: 0;
  left: 50%;
  padding-top: 32px;
}

.tooltip-component-dropdown__content {
  border-radius: 4px;
  padding: 8px 12px;
  max-width: 300px;
  text-align: left;
  background-color: #fff;
  font-size: 0.9rem;
  font-weight: 400;
  color: #767676;
  box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
  -webkit-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
  -moz-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
}

.tooltip-component:hover,
.tooltip-component.open {
  .tooltip-component-dropdown {
    animation: tooltipFadeIn 0.15s;
    display: contents;
  }
}

// large and desktop
@media (min-width: 992px) {
  .tooltip-component-dropdown {
    display: none;
    position: absolute;
    top: 0;
    left: 86%;
    transform: translateX(-50%);
    padding-top: 32px;
  }

  .tooltip-component-dropdown__content {
    border-radius: 4px;
    padding: 8px 12px;
    width: 300px;
    text-align: left;
    background-color: #fff;
    font-size: 0.9rem;
    font-weight: 400;
    color: #767676;
    box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
    -webkit-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
    -moz-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
  }

  .tooltip-component-dropdown__content[location="left"] {
    position: absolute;
    top: 50%;
    transform: translateX(-100%);
  }
}

@media (max-width: 992px) {
  .tooltip-component-dropdown__content[location="left"] {
    position: absolute;
    top: 50%;
    transform: translateX(-100%);
    min-width: 300px;
  }
}

@media (max-width: 576.98px) {
  .tooltip-component .tooltip-component-dropdown .tooltip-component-dropdown__content,
  .tooltip-component-dropdown__content[location="left"] {
    right: 0;
    left: 0;
    top: 0;
    bottom: 0;
    max-width: calc(100%);
    height: fit-content;
    position: fixed;
    margin: 0 auto;
    margin: 13px auto;
  }

  .tooltip-component-dropdown__content[location="left"] {
    transform: translateX(0%);
  }

  .tooltip-component.open .tooltip-component__label {
    position: absolute;
    top: 0;
    right: 0;
  }
  .tooltip-component.open:is(.right-auto) .tooltip-component__label {
    right: auto;
  }

  .tooltip-component.open:is(.right-zero) .tooltip-component__label {
    right: 0;
  }
}
</style>
